/**
 * Image mapping system using ES module imports for production compatibility
 * Maps JSON paths to imported asset URLs that work in both dev and production
 */

// Import all images as ES modules
import logoMain from "@/assets/images/Logo-Dianas-Morning-Brew.png";
import logoHeader from "@/assets/images/Logo-Dianas-Morning-Brew-300x158.png";
import logoSticky from "@/assets/images/Logo-Dianas-Morning-Brew-180px.png";
import heroBackground from "@/assets/images/barista-pours-hot-water-into-the-glass-with-coffee-RKNLX2V.jpg";
import headerOverlay from "@/assets/images/bg-header-overlay-lg-4-e1591101121360.png";
import coffeeBeans from "@/assets/images/Dianas-NHRoastingCo-CoffeeBeans.jpg";
import eddyTheEgg from "@/assets/images/Dianas-EddyTheEgg-Drawing-1.jpg";
import freshBreadParallax from "@/assets/images/Dianas-FreshBread-Paralax.jpg";
import storefrontWindowArt from "@/assets/images/Dianas-Photos-Egg-WindowArt-950x1024.jpg";

// Gallery images
import newsStoryGallery from "@/assets/images/Dianas-Photos-NewsStory-610x610.jpg";
import freshBreadGallery from "@/assets/images/Dianas-FreshBread-Paralax-610x610.jpg";
import pastriesGallery2 from "@/assets/images/Dianas-Photos-FreshBakedPastries-2-610x610.jpg";
import pastriesGallery1 from "@/assets/images/Dianas-Photos-FreshBakedPastries-610x610.jpg";

/**
 * Image mapping from JSON paths to imported asset URLs
 * This ensures proper asset handling in both development and production builds
 */
const imageMap: Record<string, string> = {
  // Diana's Morning Brew logos - multiple path mappings for flexibility
  "/wp-content/uploads/Logo-Dianas-Morning-Brew.png": logoMain,
  "/images/Logo-Dianas-Morning-Brew.png": logoMain,
  "/assets/images/Logo-Dianas-Morning-Brew.png": logoMain,
  
  "/wp-content/uploads/Logo-Dianas-Morning-Brew-300x158.png": logoHeader,
  "/images/Logo-Dianas-Morning-Brew-300x158.png": logoHeader,
  "/assets/images/Logo-Dianas-Morning-Brew-300x158.png": logoHeader,
  
  "/wp-content/uploads/Logo-Dianas-Morning-Brew-180px.png": logoSticky,
  "/images/Logo-Dianas-Morning-Brew-180px.png": logoSticky,
  "/assets/images/Logo-Dianas-Morning-Brew-180px.png": logoSticky,
  
  // Hero section images
  "/wp-content/uploads/barista-pours-hot-water-into-the-glass-with-coffee-RKNLX2V.jpg": heroBackground,
  "/images/hero-background.jpg": heroBackground,
  "/assets/images/hero-background.jpg": heroBackground,
  
  "/wp-content/uploads/bg-header-overlay-lg-4-e1591101121360.png": headerOverlay,
  "/images/header-overlay.png": headerOverlay,
  "/assets/images/header-overlay.png": headerOverlay,
  
  // About/Coffee section images
  "/wp-content/uploads/Dianas-NHRoastingCo-CoffeeBeans.jpg": coffeeBeans,
  "/images/coffee-beans.jpg": coffeeBeans,
  "/assets/images/coffee-beans.jpg": coffeeBeans,
  
  "/wp-content/uploads/Dianas-EddyTheEgg-Drawing-1.jpg": eddyTheEgg,
  "/images/eddy-the-egg.jpg": eddyTheEgg,
  "/assets/images/eddy-the-egg.jpg": eddyTheEgg,
  
  // Fresh Bread section images
  "/wp-content/uploads/Dianas-FreshBread-Paralax.jpg": freshBreadParallax,
  "/images/fresh-bread-parallax.jpg": freshBreadParallax,
  "/assets/images/fresh-bread-parallax.jpg": freshBreadParallax,
  
  "/wp-content/uploads/Dianas-Photos-Egg-WindowArt-950x1024.jpg": storefrontWindowArt,
  "/images/storefront-window-art.jpg": storefrontWindowArt,
  "/assets/images/storefront-window-art.jpg": storefrontWindowArt,
  
  // Gallery images
  "/wp-content/uploads/Dianas-Photos-NewsStory-610x610.jpg": newsStoryGallery,
  "/images/news-story-gallery.jpg": newsStoryGallery,
  "/assets/images/news-story-gallery.jpg": newsStoryGallery,
  
  "/wp-content/uploads/Dianas-FreshBread-Paralax-610x610.jpg": freshBreadGallery,
  "/images/fresh-bread-gallery.jpg": freshBreadGallery,
  "/assets/images/fresh-bread-gallery.jpg": freshBreadGallery,
  
  "/wp-content/uploads/Dianas-Photos-FreshBakedPastries-2-610x610.jpg": pastriesGallery2,
  "/images/pastries-gallery-2.jpg": pastriesGallery2,
  "/assets/images/pastries-gallery-2.jpg": pastriesGallery2,
  
  "/wp-content/uploads/Dianas-Photos-FreshBakedPastries-610x610.jpg": pastriesGallery1,
  "/images/pastries-gallery-1.jpg": pastriesGallery1,
  "/assets/images/pastries-gallery-1.jpg": pastriesGallery1,
};

/**
 * Resolves image paths from JSON data to actual imported asset URLs
 * @param imagePath - Path from JSON data (e.g., "/assets/images/logo.png")
 * @returns Resolved asset URL that works in both dev and production
 */
export function resolveImagePath(imagePath: string): string {
  // First try exact match in image map
  if (imageMap[imagePath]) {
    return imageMap[imagePath];
  }

  // Try normalized paths (with and without /assets prefix)
  const normalizedPath = imagePath.startsWith("/assets/")
    ? imagePath
    : `/assets${imagePath.startsWith("/") ? imagePath : "/" + imagePath}`;

  if (imageMap[normalizedPath]) {
    return imageMap[normalizedPath];
  }

  // Try legacy path format
  const legacyPath = imagePath.replace("/assets", "");
  if (imageMap[legacyPath]) {
    return imageMap[legacyPath];
  }

  // Fallback: log warning and return original path
  console.warn(`Image not found in image map: ${imagePath}. Available images:`, Object.keys(imageMap));
  return imagePath;
}

/**
 * Get all available image paths for debugging
 */
export function getAvailableImages(): string[] {
  return Object.keys(imageMap);
}

/**
 * Legacy export for compatibility
 */
export const resolveImageUrl = resolveImagePath;
